import { AfterViewInit, DestroyRef, Directive, ElementRef, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HisTableContextDirective } from '../../table/directive/his-table-context.directive';

@Directive({
    selector: '[hisFrozencol]',
    standalone: false
})
export class HisFrozenColDirective implements AfterViewInit, OnChanges, OnDestroy {
    private el = inject(ElementRef);
    private renderer = inject(Renderer2);
    private readonly destroyRef = inject( DestroyRef ); 

    private readonly tableContext = inject( HisTableContextDirective, { optional: true } );

    @Input( 'hisFrozencol' ) frozenCols!: string;

    private mutationObserver!: MutationObserver;
    private resizeObserver?: ResizeObserver;
    private pendingApply = false;

    ngAfterViewInit() {
        const element = this.el.nativeElement;
        element.style.setProperty( 'display', 'block' );

        if ( this.tableContext ) {
            this.tableContext.tableConfigStream.pipe( takeUntilDestroyed( this.destroyRef ) ).subscribe( config => {
                if ( config.frozenCol !== this.frozenCols ) {
                    this.frozenCols = config.frozenCol;
                    this.refresh();
                }
            } );
        }
        
        this.mutationObserver = new MutationObserver( () => {
            const hasTdAndThs = !!element.querySelector('td, th');
            if (!hasTdAndThs || this.frozenCols == undefined || this.frozenCols == '' || this.frozenCols == null) {
                return;
            }

            if (this.pendingApply) {
                this.applyFrozenColumns();
                this.pendingApply = false;
            }
        });

        this.mutationObserver.observe(element, {
            childList: true,
            subtree: true
        });

        // refresh frozen columns position on resize
        this.resizeObserver = new ResizeObserver(() => this.refresh());
        this.resizeObserver.observe(element);
        
        this.pendingApply = true;
        this.tryApplyImmediately();
    }

    ngOnDestroy(): void {
        this.mutationObserver?.disconnect();
        this.resizeObserver?.disconnect();
    }
    ngOnChanges(changes: SimpleChanges) {
        if (!changes.frozenCols) {
            return;
        }
        this.pendingApply = true;
        this.tryApplyImmediately();
    }
	
    public refresh(): void {
        this.pendingApply = true;
        this.tryApplyImmediately();
    }

    private tryApplyImmediately() {
        const element = this.el.nativeElement;
        const hasTdAndThs = !!element.querySelector('td, th');
        if (hasTdAndThs) {
            this.applyFrozenColumns();
            this.pendingApply = false;
        }
    }

    private applyFrozenColumns() {
        const element = this.el.nativeElement;

        this.renderer.removeClass(element, 'frozenCol');
        this.renderer.removeClass(element, 'frozenCol_2');
        this.renderer.removeClass(element, 'frozenCol_3');

        element.querySelectorAll('td, th').forEach((el: any) => {
            this.renderer.removeStyle(el, 'left');
        });
        if (!this.frozenCols || this.frozenCols === 'noneCol') { 
            return; 
        }
        if (this.frozenCols === '3') {
            this.renderer.addClass(element, 'frozenCol_3');

            const width1 = element.querySelector('th:nth-child(1)')?.offsetWidth ?? 0;
            const width2 = element.querySelector('th:nth-child(2)')?.offsetWidth ?? 0;

            element.querySelectorAll('td:nth-child(2), th:nth-child(2)')
                .forEach((el: any) => this.renderer.setStyle(el, 'left', width1 + 'px'));

            element.querySelectorAll('td:nth-child(3), th:nth-child(3)')
                .forEach((el: any) => this.renderer.setStyle(el, 'left', (width1 + width2) + 'px'));

        } else if (this.frozenCols === '2') {
            this.renderer.addClass(element, 'frozenCol_2');

            const width1 = element.querySelector('th:nth-child(1)')?.offsetWidth ?? 0;

            element.querySelectorAll('td:nth-child(2), th:nth-child(2)')
                .forEach((el: any) => this.renderer.setStyle(el, 'left', width1 + 'px'));

        } else if (this.frozenCols === '1') {
            this.renderer.addClass(element, 'frozenCol');
        }
    }
}
